import { useEffect, useState } from "react";
import { Copy, Facebook, CreditCard, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import TabHeader from "@/components/admin/TabHeader";
import { field } from "@/pages/admin/AdminLogin";

function Card({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid} className="bg-paper p-6">
      <div className="mb-4 flex items-center gap-3"><Icon className="h-5 w-5 text-clay" strokeWidth={1.5} /><h2 className="font-display text-2xl font-extralight text-ink">{title}</h2></div>
      <div className="font-body text-sm leading-relaxed text-stone">{children}</div>
    </section>
  );
}

function Shipping() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS({ ...r.data, free_shipping_over: r.data.free_shipping_over ?? "" })); }, []);
  if (!s) return null;
  const save = async () => {
    try {
      await api.put("/admin/settings", { shipping_flat: Number(s.shipping_flat), free_shipping_over: s.free_shipping_over === "" ? null : Number(s.free_shipping_over), ship_countries: s.ship_countries });
      toast.success("Shipping saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card icon={Truck} title="Shipping" testid="settings-shipping">
      <div className="grid gap-4 sm:grid-cols-3">
        <label><span className="overline mb-2 block text-[0.6rem] text-muted">Flat rate (USD)</span><input data-testid="settings-shipping-flat" type="number" min="0" step="0.01" value={s.shipping_flat} onChange={(e) => setS({ ...s, shipping_flat: e.target.value })} className={field} /></label>
        <label><span className="overline mb-2 block text-[0.6rem] text-muted">Free over (empty = never)</span><input data-testid="settings-free-over" type="number" min="0" step="0.01" value={s.free_shipping_over} onChange={(e) => setS({ ...s, free_shipping_over: e.target.value })} className={field} /></label>
        <label><span className="overline mb-2 block text-[0.6rem] text-muted">Ship to (country codes)</span><input data-testid="settings-countries" value={s.ship_countries.join(", ")} onChange={(e) => setS({ ...s, ship_countries: e.target.value.split(",").map((c) => c.trim().toUpperCase()).filter(Boolean) })} className={field} /></label>
      </div>
      <button type="button" data-testid="settings-shipping-save" onClick={save} className="mt-4 bg-ink px-5 py-2.5 text-paper"><span className="overline">Save shipping</span></button>
    </Card>
  );
}

export default function SettingsTab() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/admin/connections").then((r) => setC(r.data)); }, []);
  const copy = async () => {
    try { await navigator.clipboard.writeText(c.feed_url); } catch { /* fallback below */ }
    toast.success("Feed link copied");
  };
  return (
    <div data-testid="admin-settings-tab" className="space-y-4">
      <TabHeader title="Shipping & connections" note="Your accounts are connected by you, on their own secure sites — no passwords are ever stored here." />
      <Shipping />
      <Card icon={Facebook} title="Facebook & Instagram Shop" testid="settings-meta">
        <p>Your store publishes a live product feed. Paste it into Meta once and every live item (with a price) appears in your Facebook & Instagram shop, so you can tag products in posts with a <b>Shop now</b> button that opens the piece on this site.</p>
        <div className="mt-4 flex gap-2">
          <input data-testid="settings-feed-url" readOnly value={c?.feed_url || ""} onFocus={(e) => e.target.select()} className={`${field} font-mono text-xs`} />
          <button type="button" data-testid="settings-feed-copy" onClick={copy} className="inline-flex shrink-0 items-center gap-2 bg-ink px-4 text-paper"><Copy className="h-4 w-4" /><span className="overline">Copy</span></button>
        </div>
        <ol className="mt-4 list-decimal space-y-1.5 pl-5">
          <li>Open <a className="underline" href="https://business.facebook.com/commerce" target="_blank" rel="noreferrer">Meta Commerce Manager</a> and sign in with your business Facebook account.</li>
          <li>Create a catalog (type: <i>E-commerce</i>) → <i>Data sources</i> → <i>Data feed</i> → <i>Scheduled feed</i>.</li>
          <li>Paste the feed link above, choose <i>Daily</i> (or hourly), currency USD.</li>
          <li>Under <i>Shops</i>, connect the catalog to your Facebook Page and Instagram account, with checkout on your website.</li>
          <li>Once Meta approves the shop, tag products when you post — the button sends buyers here.</li>
        </ol>
      </Card>
      <Card icon={CreditCard} title="Payments (Stripe)" testid="settings-stripe">
        <p>Checkout runs through Stripe. Mode: <b data-testid="settings-stripe-mode">{c?.stripe_mode === "live" ? "Live" : "Test (no real charges yet)"}</b>.</p>
        <p className="mt-2">To take real payments, claim your Stripe account from the link Stripe emailed you (also under <i>Manage → Payments</i> in Emergent), then add your Chase business account as the payout bank inside Stripe. Payouts land in Chase automatically.</p>
      </Card>
    </div>
  );
}
